import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { IGroup, IGroupItems } from '../../types/customGroupingTypes';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { PlAN_TYPES } from '@constants/individualPortal/customGrouping/customGrouping.constants';
import UnenrolledGroupCard from './UnenrolledGroupCard';
import { configs } from '@constants/configs/configs';
import useCustomGrouping from '../../hooks/useCustomGrouping';
import { useAppDispatch, useAppSelector, useModal } from '@hooks/index';
import CapitalizedButton from '../common/CapitalizedButton';

interface IEnrolledMembersWrapperProps {
    unenrolledGroups: IGroupItems[];
    planType: PlAN_TYPES;
    /** ID of the element that provides group context for screen readers (e.g., "Shop for these members") */
    groupLabelId?: string;
}

export default function UnenrolledMembersWrapper(props: IEnrolledMembersWrapperProps) {
    const { unenrolledGroups, planType, groupLabelId } = props;

    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && groupLabelId) {
            const element = document.getElementById(groupLabelId);
            if (!element) {
                console.warn(
                    `UnenrolledMembersWrapper: groupLabelId "${groupLabelId}" does not reference an existing element in the DOM`
                );
            }
        }
    }, [groupLabelId]);

    const customGroupingState = useAppSelector(state => state.individualPortal.customGrouping);
    const { caseNumber, applicationYear } = customGroupingState;
    const dispatch = useAppDispatch();
    const {
        shopForUnenrolledGroup,
        isAnyMembersUnEnrolledInHealth,
        isAnyMembersUnEnrolledInDental,
        areAllMembersAddedToGroups,
    } = useCustomGrouping({ dispatch });
    const { openModal } = useModal();

    const [unenrolledGroupSelected, setUnenrolledGroupSelected] = useState(
        () => {
            const state = {};
            unenrolledGroups.forEach(group => {
                state[group.id] = {
                    ...group,
                    memberList: group.memberList.filter(member => member.isChecked),
                };
            });
            return state;
        }
    );

    const navigate = useNavigate();

    const isUnenrolledMemberVisible = group => {
        if (planType === PlAN_TYPES.HEALTH) {
            return isAnyMembersUnEnrolledInHealth(group) && !areAllMembersAddedToGroups(group);
        } else {
            return isAnyMembersUnEnrolledInDental(group) && !areAllMembersAddedToGroups(group);
        }
    };

    function onContinue() {
        const allGroups:IGroup[] = Object.values(unenrolledGroupSelected);
        const group: IGroup = allGroups.find(group => group.memberList.length) as IGroup;
        const selectedMembers = Object.values(unenrolledGroupSelected).reduce((acc, group) => {
            acc.push(...group.memberList);
            return acc;
        }, []);

        const onContinue = selectedCoverageDate =>
            shopForUnenrolledGroup(
                {
                    group: {
                        ...group,
                        memberList: selectedMembers,
                    },
                    enablePopupForEndOfPHE: false,
                    isChangePlan: false,
                    typeOfPlan: planType,
                    caseNumber,
                },
                { openModal, selectedCoverageDate },
            );

        const { enablePopupForPregnancyQle, earliestStartDate, nextAvailableDate } = group;

        if (enablePopupForPregnancyQle && earliestStartDate && nextAvailableDate) {
            openModal('selectCoverageDate', {
                coverageDate1: earliestStartDate,
                coverageDate2: nextAvailableDate,
                onContinue,
            });
        } else onContinue(nextAvailableDate || earliestStartDate);
    }

    const numSelectedMembers = Object.values(unenrolledGroupSelected).reduce((acc, group) => {
        acc += group?.memberList?.length;
        return acc;
    }, 0);

    return (
        <>
            {unenrolledGroups.map((group) => (
                <ConditionalRendering key={group.id} isTrue={isUnenrolledMemberVisible(group)}>
                    <UnenrolledGroupCard
                        group={group}
                        planType={planType}
                        unenrolledGroupSelected={unenrolledGroupSelected}
                        setUnenrolledGroupSelected={setUnenrolledGroupSelected}
                        groupLabelId={groupLabelId}
                    />
                </ConditionalRendering>
            ))}
            <div className="row-fluid mt-4">
                <CapitalizedButton
                    id={'go_to_dashboard'}
                    labelId="button.go_to_dashboard"
                    variant="primary"
                    addlClassNames="pull-left bold-button"
                    onClickHandler={() => navigate(configs.routes.INDPORTAL)}
                />
                <CapitalizedButton
                    id={'unenrolled_continue_button'}
                    labelId={
                        planType === PlAN_TYPES.HEALTH
                            ? 'customGrouping.shopHealthPlansButton'
                            : 'customGrouping.shopDentalPlansButton'
                    }
                    labelValue={{ secondarySelections: numSelectedMembers, coverageYear: applicationYear }}
                    variant="primary"
                    addlClassNames="pull-right bold-button"
                    onClickHandler={onContinue}
                    isDisabled={!Object.values(unenrolledGroupSelected).some(group => group?.memberList?.length)}
                />
            </div>
        </>
    );
}
